import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { ProgramEntity } from "../../../../services/programs/programs.types";
import {
  AvailableCourse,
  DosificationSlot,
  SlotCourseRef,
} from "../../../../services/school-dosification/school-dosification.types";
import { useSchoolDosification } from "../../../../hooks/useSchoolDosification";
import CourseSelectorModal from "./CourseSelectorModal";
import SlotCoursesCell from "./SlotCoursesCell";
import {
  appendCourse,
  courseIdsBlockedInWeek,
  removeCourseAt,
  replaceCourseAt,
  toSlotCourseRefs,
} from "./DosificationTab.utils";

const { Title, Text } = Typography;

interface DosificationTabProps {
  schoolId: number;
  schoolName: string;
  /** Programas que la escuela tiene contratados. */
  licensedPrograms: ProgramEntity[];
}

/**
 * SCRUM-804: dosificación personalizada por escuela.
 *
 * La escuela conserva sus grados y asignaturas; lo único que se cambia es qué
 * curso ocupa cada semana. Las semanas que se listan son las que la
 * dosificación de Emmi ya define para ese programa y trimestre, más las que la
 * escuela añadió (SCRUM-1534): el backend las numera, acá sólo se piden.
 *
 * El segundo selector elige un PROGRAMA, no una asignatura: dos programas
 * pueden compartir grado y asignatura (Lectoescritura y su variante Script), y
 * colapsarlos por asignatura haría imposible configurar una de las dos.
 *
 * SCRUM-1076: a week can hold several courses, in order. Every edit sends the
 * whole list of the week and reloads what the backend saved.
 */
const DosificationTab: React.FC<DosificationTabProps> = ({
  schoolId,
  schoolName,
  licensedPrograms,
}) => {
  const [gradeId, setGradeId] = useState<number | null>(null);
  const [programId, setProgramId] = useState<number | null>(null);
  const [trimester, setTrimester] = useState(1);
  const [slotBeingEdited, setSlotBeingEdited] = useState<{
    slot: DosificationSlot;
    /** Course being replaced; `null` appends one at the end of the week. */
    replaceIndex: number | null;
  } | null>(null);

  const {
    configuration,
    loading,
    saving,
    loadConfiguration,
    saveSlotCourses,
    addWeek,
    restoreEmmi,
  } = useSchoolDosification(schoolId);

  const grades = useMemo(() => {
    const byId = new Map<number, string>();
    licensedPrograms.forEach((program) => {
      if (program.grade) byId.set(program.grade.id, program.grade.grade);
    });
    return Array.from(byId.entries()).map(([id, grade]) => ({ id, grade }));
  }, [licensedPrograms]);

  const gradePrograms = useMemo(
    () =>
      licensedPrograms.filter((program) => program.grade?.id === gradeId),
    [licensedPrograms, gradeId]
  );

  useEffect(() => {
    if (gradeId === null && grades.length > 0) setGradeId(grades[0].id);
  }, [grades, gradeId]);

  useEffect(() => {
    // Al cambiar de grado, el programa elegido puede no existir en el nuevo.
    if (gradePrograms.some((program) => program.id === programId)) return;
    setProgramId(gradePrograms[0]?.id ?? null);
  }, [gradePrograms, programId]);

  useEffect(() => {
    if (programId !== null) void loadConfiguration(programId, trimester);
  }, [programId, trimester, loadConfiguration]);

  const programOptions = useMemo(() => {
    const subjectCount = new Map<number, number>();
    gradePrograms.forEach((program) =>
      subjectCount.set(
        program.subject_id,
        (subjectCount.get(program.subject_id) ?? 0) + 1
      )
    );

    return gradePrograms.map((program) => ({
      value: program.id,
      // El título sólo se agrega cuando la asignatura tiene más de un programa
      // en el grado, que es lo que distingue a las variantes entre sí.
      label:
        (subjectCount.get(program.subject_id) ?? 0) > 1
          ? `${program.subject?.name} — ${program.title}`
          : (program.subject?.name ?? program.title),
    }));
  }, [gradePrograms]);

  // Courses of the week the catalogue must not offer again (S5).
  const blockedCourseIds = useMemo(
    () =>
      slotBeingEdited
        ? courseIdsBlockedInWeek(
            slotBeingEdited.slot.assigned_courses,
            slotBeingEdited.replaceIndex
          )
        : [],
    [slotBeingEdited]
  );

  const saveWeek = (
    slot: DosificationSlot,
    courses: SlotCourseRef[],
    fallbackMessage: string
  ) => {
    if (programId === null) return Promise.resolve(false);
    return saveSlotCourses(
      programId,
      { trimester, week: slot.week, courses },
      fallbackMessage
    );
  };

  const onCourseSelected = async (course: AvailableCourse) => {
    if (slotBeingEdited === null) return;

    const { slot, replaceIndex } = slotBeingEdited;
    const picked = {
      course_id: course.course_id,
      source_program_id: course.source_program.id,
    };
    const saved = await saveWeek(
      slot,
      replaceIndex === null
        ? appendCourse(slot.assigned_courses, picked)
        : replaceCourseAt(slot.assigned_courses, replaceIndex, picked),
      "No se pudo asignar el curso a esa semana."
    );

    if (saved) setSlotBeingEdited(null);
  };

  if (licensedPrograms.length === 0) {
    return (
      <Alert
        type="info"
        showIcon
        message="Esta escuela todavía no tiene programas contratados"
        description="Asigná programas desde la pestaña Escuela para poder personalizar su dosificación."
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Title level={4} style={{ marginBottom: 4 }}>
          Dosificación personalizada
        </Title>
        <Text type="secondary">
          Definí qué cursos ve {schoolName} en cada semana: puede ser más de
          uno, y se muestran en la plataforma en el mismo orden que acá. Se
          pueden usar cursos de la misma asignatura de cualquier grado, y todos
          adoptan la semana de destino.
        </Text>
      </div>

      <Space wrap size="middle" align="end">
        <Space direction="vertical" size={4}>
          <Text strong>Grado</Text>
          <Select
            style={{ minWidth: 180 }}
            value={gradeId ?? undefined}
            onChange={setGradeId}
            options={grades.map((grade) => ({
              value: grade.id,
              label: grade.grade,
            }))}
          />
        </Space>

        <Space direction="vertical" size={4}>
          <Text strong>Asignatura</Text>
          <Select
            style={{ minWidth: 260 }}
            value={programId ?? undefined}
            onChange={setProgramId}
            options={programOptions}
          />
        </Space>

        <Space direction="vertical" size={4}>
          <Text strong>Trimestre</Text>
          <Segmented
            value={trimester}
            onChange={(value) => setTrimester(Number(value))}
            options={[
              { value: 1, label: "T1" },
              { value: 2, label: "T2" },
              { value: 3, label: "T3" },
            ]}
          />
        </Space>

        <Space direction="vertical" size={4}>
          <Text strong>&nbsp;</Text>
          <Popconfirm
            title="Restaurar la dosificación de Emmi"
            description="Se borran los cambios de esta asignatura en los tres trimestres."
            okText="Restaurar"
            cancelText="Cancelar"
            disabled={!configuration?.is_custom || saving}
            onConfirm={() => {
              if (programId !== null) void restoreEmmi(programId, trimester);
            }}
          >
            <Button
              icon={<ReloadOutlined />}
              disabled={!configuration?.is_custom || saving}
            >
              Restaurar dosificación Emmi
            </Button>
          </Popconfirm>
        </Space>
      </Space>

      {configuration && (
        <Tag color={configuration.is_custom ? "purple" : "default"}>
          {configuration.is_custom
            ? "Dosificación personalizada"
            : "Usando dosificación Emmi"}
        </Tag>
      )}

      <Table
        rowKey="week"
        size="middle"
        loading={loading}
        dataSource={configuration?.slots ?? []}
        pagination={false}
        // Las semanas de un trimestre no entran en pantalla: la tabla scrollea
        // en vez de paginar, para no cortar la secuencia en páginas.
        scroll={{ y: 460 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Este programa no tiene semanas en el trimestre seleccionado"
            />
          ),
        }}
        columns={[
          {
            title: "Semana",
            dataIndex: "week",
            width: 110,
            render: (week: string, slot) => (
              <Space size={4}>
                <Text strong>{week}</Text>
                {slot.is_diagnostic && <Tag color="green">Diagnóstico</Tag>}
              </Space>
            ),
          },
          {
            title: "Curso asignado",
            render: (_, slot) => (
              <SlotCoursesCell
                slot={slot}
                saving={saving}
                onChange={(index) =>
                  setSlotBeingEdited({ slot, replaceIndex: index })
                }
                onRemove={(index) =>
                  void saveWeek(
                    slot,
                    removeCourseAt(slot.assigned_courses, index),
                    "No se pudo quitar el curso de esa semana."
                  )
                }
                onReorder={(courses) => {
                  // The list stays draggable while a save is in flight; a drop
                  // then would start from a week that is about to change.
                  if (saving) return;
                  void saveWeek(
                    slot,
                    toSlotCourseRefs(courses),
                    "No se pudo cambiar el orden de los cursos de esa semana."
                  );
                }}
              />
            ),
          },
          {
            title: "En la dosificación Emmi",
            width: 240,
            render: (_, slot) =>
              slot.emmi_courses.length === 0 ? (
                <Text type="secondary">—</Text>
              ) : (
                <Text type="secondary">
                  {slot.emmi_courses.map((course) => course.title).join(", ")}
                </Text>
              ),
          },
          {
            title: "Acciones",
            width: 210,
            render: (_, slot) => {
              const hasCourses = slot.assigned_courses.length > 0;
              // Diagnostic weeks hold exactly one course.
              if (hasCourses && !slot.allows_multiple_courses) return null;

              return (
                <Button
                  size="small"
                  disabled={saving}
                  onClick={() =>
                    setSlotBeingEdited({ slot, replaceIndex: null })
                  }
                >
                  {hasCourses ? "+ Añadir otra secuencia" : "Añadir curso"}
                </Button>
              );
            },
          },
        ]}
      />

      {configuration?.can_add_week && programId !== null && (
        <Button
          disabled={saving}
          onClick={() => void addWeek(programId, trimester)}
        >
          + Añadir semana
        </Button>
      )}

      {programId !== null && (
        <CourseSelectorModal
          open={slotBeingEdited !== null}
          schoolId={schoolId}
          programId={programId}
          slotTrimester={trimester}
          week={slotBeingEdited?.slot.week ?? null}
          isDiagnosticSlot={slotBeingEdited?.slot.is_diagnostic ?? false}
          blockedCourseIds={blockedCourseIds}
          saving={saving}
          onCancel={() => setSlotBeingEdited(null)}
          onSelect={(course) => void onCourseSelected(course)}
        />
      )}
    </Space>
  );
};

export default DosificationTab;
